import { useQuery, useQueryClient } from '@tanstack/react-query';
import { UzButton } from '@utazzo/ui';
import { BellOff, BellRing, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';

import { DAY_MS, isPushBannerSnoozed, pushBannerState, snoozePushBanner } from '../push-banner';
import { getPushConfig, sendTestPush } from '../push.api';
import {
  enablePushOnThisDevice,
  isOptedInHere,
  isPushActiveHere,
  onPushActiveChange,
  pushSupport,
  readyRegistration,
} from '../web-push-client';

import { primeMicrophone } from '@/features/whatsapp/mic-permission';
import { useCanTakeWhatsAppCalls } from '@/features/whatsapp/use-can-take-calls';
import { isAppleMobile, isPhoneDevice, isStandaloneApp } from '@/lib/device';

const permissionNow = (): NotificationPermission | 'unsupported' =>
  typeof window !== 'undefined' && 'Notification' in window
    ? Notification.permission
    : 'unsupported';

/** Where to allow notifications again after a "Block" - per platform. */
const unblockSteps = (): string[] => {
  if (isAppleMobile()) {
    return [
      'Open iPhone Settings → Notifications.',
      'Tap Utazzo CRM and turn on Allow Notifications.',
      'Come back here and tap “Check again”.',
    ];
  }
  if (isPhoneDevice()) {
    return [
      'Touch and hold the Utazzo CRM icon → App info.',
      'Open Notifications and allow them.',
      'Come back here and tap “Check again”.',
    ];
  }
  return [
    'Open the app menu (⋮) in this window’s title bar → App info / Site settings.',
    'Set Notifications to Allow.',
    'Come back here and tap “Check again”.',
  ];
};

/**
 * "Turn on notifications" nudge for the INSTALLED app only (a browser tab gets
 * the install banner instead, and keeps My profile → Phone notifications).
 * The browser's own permission prompt only appears from a tap - iPhone refuses
 * it otherwise - so this offers the button rather than prompting on open.
 * Reuses exactly the enable flow of the Settings card.
 */
export const PushPermissionBanner = (): React.JSX.Element | null => {
  const qc = useQueryClient();
  const standalone = isStandaloneApp();
  const support = pushSupport();
  const [snoozed, setSnoozed] = useState(() => isPushBannerSnoozed());
  const [permission, setPermission] = useState(permissionNow);
  const [registration, setRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [busy, setBusy] = useState(false);
  const canTakeCalls = useCanTakeWhatsAppCalls();
  // Re-render when push flips on/off anywhere (Settings card, sync).
  useSyncExternalStore(onPushActiveChange, isPushActiveHere, () => false);

  const eligible = standalone && support === 'supported' && !snoozed;
  // Same key as the Settings card - one request, shared cache.
  const config = useQuery({
    queryKey: ['push', 'config'],
    queryFn: getPushConfig,
    enabled: eligible,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  const state = pushBannerState({
    standalone,
    support,
    serverReady: Boolean(config.data?.enabled && config.data.publicKey),
    permission,
    optedInHere: isOptedInHere(),
    snoozed,
  });

  // The tap must reach the prompt without awaiting anything first (Safari
  // drops the gesture across an await), so load the worker up front.
  useEffect(() => {
    if (state === 'hidden') return;
    let alive = true;
    void readyRegistration().then((reg) => {
      if (alive) setRegistration(reg);
    });
    return () => {
      alive = false;
    };
  }, [state]);

  const turnOn = useCallback((): void => {
    const publicKey = config.data?.publicKey;
    if (!publicKey || !registration) return;
    setBusy(true);
    void enablePushOnThisDevice(publicKey, registration)
      .then((result) => {
        if (result.ok) {
          toast.success('Notifications are on. Sending a test…');
          // Proof it works, straight away. Best-effort.
          sendTestPush().catch(() => undefined);
          void qc.invalidateQueries({ queryKey: ['push', 'devices'] });
          // Ask for the microphone now, not while a call is ringing.
          if (canTakeCalls) void primeMicrophone();
          return;
        }
        if (result.reason !== 'denied') {
          toast.error(result.message ?? 'Could not turn on notifications. Try again.');
        }
      })
      .finally(() => {
        setBusy(false);
        setPermission(permissionNow());
      });
  }, [config.data?.publicKey, registration, qc, canTakeCalls]);

  // Back from the phone's Settings app: pick up a changed permission. If they
  // went there to allow notifications, finish the job without another tap -
  // with permission granted, switching push on needs no gesture.
  const permissionRef = useRef(permission);
  permissionRef.current = permission;
  const turnOnRef = useRef(turnOn);
  turnOnRef.current = turnOn;
  useEffect(() => {
    if (!eligible) return;
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      const was = permissionRef.current;
      const now = permissionNow();
      setPermission(now);
      if (was === 'denied' && now === 'granted') turnOnRef.current();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [eligible]);

  const later = (): void => {
    snoozePushBanner(DAY_MS);
    setSnoozed(true);
  };

  const checkAgain = (): void => {
    const now = permissionNow();
    setPermission(now);
    if (now === 'denied') {
      toast.info('Still blocked. Allow notifications in your settings, then check again.');
      return;
    }
    // Allowed in Settings meanwhile: this tap finishes the job.
    turnOn();
  };

  if (state === 'hidden') return null;
  const blocked = state === 'blocked';

  return (
    <div
      role="dialog"
      aria-label="Turn on notifications"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-[var(--ds-border)] bg-[var(--bg)] p-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] sm:inset-x-auto sm:bottom-4 sm:left-4 sm:max-w-md sm:rounded-[var(--r-lg)] sm:border"
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[var(--r-md)] bg-[var(--z-100)] text-[var(--fg)]">
          {blocked ? (
            <BellOff className="h-5 w-5" aria-hidden />
          ) : (
            <BellRing className="h-5 w-5" aria-hidden />
          )}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[var(--fg)]">
            {blocked ? 'Notifications are blocked' : 'Turn on notifications'}
          </p>
          {blocked ? (
            <ol className="mt-1 list-decimal space-y-0.5 pl-4 text-xs text-[var(--fg-muted)]">
              {unblockSteps().map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          ) : (
            <p className="mt-0.5 text-xs text-[var(--fg-muted)]">
              Get leads, follow-ups, WhatsApp messages and calls on this device, even when Utazzo is
              closed.
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            {blocked ? (
              <UzButton size="sm" onClick={checkAgain} loading={busy}>
                Check again
              </UzButton>
            ) : (
              <UzButton size="sm" onClick={turnOn} loading={busy} disabled={!registration}>
                <BellRing className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Turn on
              </UzButton>
            )}
            <UzButton size="sm" variant="ghost" onClick={later}>
              Later
            </UzButton>
          </div>
        </div>
        <button
          type="button"
          onClick={later}
          aria-label="Dismiss notifications banner"
          className="-mt-1 -mr-1 flex-shrink-0 rounded-[var(--r-md)] p-1 text-[var(--fg-faint)] hover:text-[var(--fg)]"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  );
};
